import { useRef, useState } from 'react';
import { apiGetRaw, apiJson } from '../api/client';

interface ImportStats {
  settings_upserted: number;
  pins_upserted: number;
  presets_upserted: number;
  skipped: string[];
}

interface ImportResponse {
  ok: true;
  stats: ImportStats;
}

/**
 * Stage 12 (F1): one-click JSON backup of user-editable config.
 * Export bundles:
 *   - admin_settings (flat key/value)
 *   - kokoro_agent_pins (Stage 7 + 8 voice pins)
 *   - voice_presets (Stage 10.1 Gemini TTS presets)
 * Import upserts by unique key. Settings keyed by setting_key; pins by
 * agent_name; presets by id.
 */
export function BackupTab({ onChanged }: { onChanged: () => void }) {
  const fileInput = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState<'idle' | 'exporting' | 'importing'>('idle');
  const [err, setErr] = useState<string | null>(null);
  const [lastStats, setLastStats] = useState<ImportStats | null>(null);

  const doExport = async (): Promise<void> => {
    setBusy('exporting');
    setErr(null);
    try {
      const res = await apiGetRaw('/api/admin/export');
      const blob = await res.blob();
      const today = new Date().toISOString().slice(0, 10);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `synth-web-config-${today}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy('idle');
    }
  };

  const doImport = async (file: File): Promise<void> => {
    setBusy('importing');
    setErr(null);
    setLastStats(null);
    try {
      const text = await file.text();
      let parsed: unknown;
      try {
        parsed = JSON.parse(text);
      } catch (parseErr) {
        throw new Error(
          `File is not valid JSON: ${
            parseErr instanceof Error ? parseErr.message : String(parseErr)
          }`,
        );
      }
      const res = await apiJson<ImportResponse>(
        'POST',
        '/api/admin/import',
        parsed as Record<string, unknown>,
      );
      setLastStats(res.stats);
      onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy('idle');
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (
      !confirm(
        `Import "${file.name}"? Settings, pins, and presets will be merged (upserted).`,
      )
    ) {
      if (fileInput.current) fileInput.current.value = '';
      return;
    }
    void doImport(file);
  };

  return (
    <div className="stack">
      <div className="panel">
        <h3>Export configuration</h3>
        <p className="muted small">
          Downloads a JSON backup of your settings, ZeroVoice pins, and
          voice presets. Speech clips, chat turns, images and other
          generated content are <strong>not</strong> included — this is a
          config backup, not a data dump.
        </p>
        <div className="voice-dropdown-row">
          <button
            type="button"
            className="btn-primary"
            onClick={() => void doExport()}
            disabled={busy !== 'idle'}
          >
            {busy === 'exporting' ? 'Exporting…' : '⬇ Download backup'}
          </button>
        </div>
      </div>

      <div className="panel">
        <h3>Import configuration</h3>
        <p className="muted small">
          Restore from a previous backup or copy config between devices.
          Existing rows are overwritten by matching key; any rows not in the
          backup are left untouched.
        </p>
        <div className="voice-dropdown-row">
          <input
            ref={fileInput}
            type="file"
            accept="application/json,.json"
            onChange={onFileChange}
            disabled={busy !== 'idle'}
          />
        </div>
        {busy === 'importing' ? (
          <div className="muted small">Importing…</div>
        ) : null}
        {lastStats ? (
          <div
            className="muted small"
            role="status"
            aria-live="polite"
            style={{ marginTop: '0.5rem' }}
          >
            Imported: {lastStats.settings_upserted} setting(s),{' '}
            {lastStats.pins_upserted} pin(s),{' '}
            {lastStats.presets_upserted} preset(s).
            {lastStats.skipped.length > 0 ? (
              <>
                {' Skipped '}
                {lastStats.skipped.length}{' '}
                <span title={lastStats.skipped.join('\n')}>
                  (hover for details)
                </span>
                .
              </>
            ) : null}
          </div>
        ) : null}
        {err ? (
          <div className="status-missing small" role="alert">
            {err}
          </div>
        ) : null}
      </div>
    </div>
  );
}
